import { useLayoutEffect } from 'react'
import { useLocation } from 'react-router-dom'

export function RevealObserver() {
  const { pathname } = useLocation()

  useLayoutEffect(() => {
    const initialElements = Array.from(document.querySelectorAll<HTMLElement>('.reveal'))
    if (!('IntersectionObserver' in window)) {
      initialElements.forEach((element) => element.classList.add('is-visible'))
      return
    }

    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        // A fast wheel/touch scroll can move a tall target from below to above
        // the viewport between observer samples. Reveal passed targets too so
        // content can never remain hidden after the visitor has reached it.
        if (entry.isIntersecting || entry.boundingClientRect.bottom < 0) {
          show(entry.target as HTMLElement)
        }
      }),
      { threshold: 0.01, rootMargin: '0px 0px -8% 0px' },
    )

    const show = (element: HTMLElement) => {
      element.classList.remove('reveal-pending')
      element.classList.add('is-visible')
      observer.unobserve(element)
    }

    const watch = (element: HTMLElement) => {
      if (element.classList.contains('is-visible') || element.classList.contains('reveal-pending')) return
      element.classList.add('reveal-pending')
      observer.observe(element)
    }

    initialElements.forEach(watch)

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return
        if (node.matches('.reveal')) watch(node)
        node.querySelectorAll<HTMLElement>('.reveal').forEach(watch)
      }))
    })
    mutationObserver.observe(document.body, { childList: true, subtree: true })

    return () => {
      mutationObserver.disconnect()
      observer.disconnect()
      document.querySelectorAll<HTMLElement>('.reveal-pending').forEach((element) => {
        element.classList.remove('reveal-pending')
        element.classList.add('is-visible')
      })
    }
  }, [pathname])

  return null
}
